import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'
import ModalOptions from '@jimmyboys/liita-ui/dist/components/ModalOptions'
import TextSearchInput from 'components/TextSearchInput/TextSearchInput'
import useDetailLevel from 'hooks/useDetailLevel'
import { useLocation } from 'react-router-dom'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import {
	controllerFilters,
	expandedCommentsAtom,
	searchProcessString,
} from 'store/atoms'
import styles from './SearchFilters.module.sass'
import { useEffect, useMemo, useState } from 'react'
import useUserProcessPreferences from 'hooks/useUserProcessPreferences'
import Tooltip from '@jimmyboys/liita-ui/dist/components/Tooltip'

const SearchFiltersMenu = (props) => {
	const { processId } = props
	const { i18n } = useGlobal()
	const location = useLocation()
	const localPathName = location.pathname.match(/([a-zA-Z-])*$/)[0]
	const [getDetailLevel, setDetailLevel] = useDetailLevel(processId)
	const detailLevel = getDetailLevel()
	const setProcessString = useSetRecoilState(searchProcessString)
	const [menuFilters, setMenuFilters] = useRecoilState(controllerFilters)

	const [filterSelected, setFilterSelected] = useState(false)

	const [getMenuPreference, setMenuExpandPreference, setMenuSortPreference] =
		useUserProcessPreferences(processId)

	const menuPreference = getMenuPreference()

	const detailLevelOptions = [
		{ value: 'summary', text: i18n`Summary` },
		{ value: 'detailed', text: i18n`Detailed` },
		{ value: 'complete', text: i18n`Complete` },
	]

	const filterDetails = {
		appointments: {
			titles: [i18n`Provider`, i18n`Specialty`, i18n`Status`],
			filters: ['provider', 'specialty', 'status'],
		},
		requisitions: {
			titles: [i18n`Requisition type`],
			filters: ['type'],
		},
	}

	useEffect(() => {
		setMenuFilters((prev) => {
			return {
				...prev,
				requisitions: {
					filters: { type: [] },
					filterValuesList: [],
				},
			}
		})
	}, [processId, setMenuFilters])

	const statusFilters = menuFilters.appointments?.filterValuesList?.[2]
	useEffect(() => {
		if (localPathName === 'appointments' && statusFilters?.length) {
			setMenuFilters((prev) => {
				const prevStatusFilters =
					prev[localPathName]?.filterValuesList?.[2]

				return {
					...prev,
					[localPathName]: {
						filterValuesList: prev[localPathName]?.filterValuesList,
						filters: {
							...prev[localPathName]?.filters,
							status: prevStatusFilters
								.map(({ value }) => value)
								.filter((value) => value !== 'Anulada'),
						},
					},
				}
			})
		}
	}, [setMenuFilters, localPathName, statusFilters?.length])

	const titles = filterDetails[localPathName]?.titles

	const disableTextSearch =
		localPathName !== 'appointments' &&
		localPathName !== 'requisitions' &&
		localPathName !== 'comments'

	const disableExpand = localPathName === 'requisitions'

	const commentsExpanded = useRecoilValue(expandedCommentsAtom)
	const setCommentsExpanded = useSetRecoilState(expandedCommentsAtom)

	useMemo(() => {
		if (
			localPathName === 'appointments' ||
			localPathName === 'requisitions'
		) {
			let tempFilterCounter = 0
			Object.values(menuFilters[localPathName]?.filters).forEach(
				(elem) => {
					tempFilterCounter += Object.values(elem).flat().length
					if (tempFilterCounter > 0) {
						setFilterSelected(true)
						return
					} else {
						setFilterSelected(false)
						return
					}
				}
			)
		}
	}, [localPathName, menuFilters])

	return (
		<HStack className={`${styles['search-filter-container']}`}>
			<TextSearchInput
				placeholder={i18n`Search`}
				className={`${styles['search-bar']} ${
					disableTextSearch && styles['--disabled']
				}`}
				onChange={(e) => setProcessString(e)}
				disabled={disableTextSearch}
			/>
			<div>
				{localPathName === 'appointments' ? (
					<Tooltip
						tint='blue'
						text={i18n`Expand appointments`}
						displayArrow={true}
						displayDelay={1500}
					>
						<ActionButton
							className={`${styles['unfold-menu-button']} ${
								menuPreference?.unfolded
									? styles['--enabled']
									: ''
							} `}
							onClick={() =>
								setMenuExpandPreference(
									!menuPreference?.unfolded
								)
							}
						>
							<Icon
								className={styles['icon']}
								width='20px'
								height='20px'
								name='unfold-more'
							></Icon>
						</ActionButton>
					</Tooltip>
				) : localPathName === 'comments' ? (
					<Tooltip
						tint='blue'
						text={
							commentsExpanded
								? i18n`Collapse all`
								: i18n`Expand all`
						}
						displayArrow={true}
						displayDelay={1500}
					>
						<ActionButton
							className={`${styles['unfold-menu-button']} ${
								commentsExpanded ? styles['--enabled'] : ''
							}`}
							onClick={() =>
								setCommentsExpanded(!commentsExpanded)
							}
						>
							<Icon
								className={styles['icon']}
								width='20px'
								height='20px'
								name='unfold-more'
							></Icon>
						</ActionButton>
					</Tooltip>
				) : (
					<ModalOptions
						className={styles['actions-modal']}
						options={detailLevelOptions}
						disabled={disableExpand}
						title={i18n`See`}
						closeAfterSelect={false}
						value={detailLevel}
						corner='straight'
						onSelect={(item) => {
							setDetailLevel(item.value)
						}}
					>
						<Tooltip
							text={i18n`Expand`}
							displayArrow={true}
							displayDelay={1500}
							tint='blue'
						>
							<ActionButton
								className={styles['unfold-menu-button-list']}
							>
								<Icon
									className={styles['icon']}
									width='20px'
									height='20px'
									name='unfold-more'
								></Icon>
							</ActionButton>
						</Tooltip>
					</ModalOptions>
				)}
			</div>
			<div>
				<ModalOptions
					className={styles['actions-modal']}
					groups={menuFilters[localPathName]?.filterValuesList}
					titles={titles}
					value={Object.values(
						menuFilters[localPathName]?.filters || [
							{ value: '', text: 'Qualquer' },
						]
					)}
					corner='straight'
					closeAfterSelect={false}
					onSelect={(item, idx) => {
						const filter =
							filterDetails[localPathName]?.filters[idx]
						if (!item.value) {
							setMenuFilters((prev) => {
								return {
									...prev,
									[localPathName]: {
										filterValuesList:
											prev[localPathName]
												?.filterValuesList,
										filters: {
											...prev[localPathName]?.filters,
											[filter]: [],
										},
									},
								}
							})
						} else {
							setMenuFilters((prev) => {
								const itemIndex = prev[localPathName]?.filters[
									filter
								].findIndex((f) => f === item.value)

								if (itemIndex >= 0) {
									return {
										...prev,
										[localPathName]: {
											filterValuesList:
												prev[localPathName]
													?.filterValuesList,
											filters: {
												...prev[localPathName]?.filters,
												[filter]: [
													...prev[
														localPathName
													]?.filters[filter].slice(
														0,
														itemIndex
													),
													...prev[
														localPathName
													]?.filters[filter].slice(
														itemIndex + 1
													),
												],
											},
										},
									}
								} else {
									return {
										...prev,
										[localPathName]: {
											filterValuesList:
												prev[localPathName]
													?.filterValuesList,
											filters: {
												...prev[localPathName]?.filters,
												[filter]: [
													...prev[localPathName]
														?.filters[filter],
													item.value,
												],
											},
										},
									}
								}
							})
						}
					}}
				>
					<Tooltip
						tint='blue'
						text={i18n`Filters`}
						displayArrow={true}
						displayDelay={1500}
					>
						<ActionButton
							className={`${styles['filter-menu-button']}  ${
								filterSelected &&
								(localPathName === 'appointments' ||
									localPathName === 'requisitions')
									? styles['--enabled']
									: ''
							}`}
							disabled={
								localPathName !== 'appointments' &&
								localPathName !== 'requisitions'
							}
						>
							<Icon
								className={styles['icon']}
								width='20px'
								height='20px'
								name='filter-alt'
							></Icon>
						</ActionButton>
					</Tooltip>
				</ModalOptions>
			</div>

			<div>
				<Tooltip
					tint='blue'
					text={i18n`Invert order`}
					displayArrow={true}
					displayDelay={1500}
				>
					<ActionButton
						className={`${styles['unfold-menu-button']} ${
							menuPreference?.order && styles['--enabled']
						}`}
						onClick={() =>
							setMenuSortPreference(!menuPreference?.order)
						}
						disabled={localPathName !== 'appointments'}
					>
						<Icon
							className={styles['icon']}
							width='20px'
							height='20px'
							name='swap'
						></Icon>
					</ActionButton>
				</Tooltip>
			</div>
		</HStack>
	)
}

export default SearchFiltersMenu
